import { computeProfitability, costBarColor, formatTracked } from '../lib/profitability';
import { formatCurrency } from '../lib/utils';
import type { Project } from '../types';

/** How much of the total project value the ClickUp-tracked time has used up. */
export default function BudgetSpent({
  project,
  hourlyCost,
  detailed = false,
}: {
  project: Project;
  hourlyCost: number | null;
  detailed?: boolean;
}) {
  if (!project.clickupListId) {
    return <span className="text-xs text-gray-300">Not linked to ClickUp</span>;
  }
  if (project.clickupSyncedAt == null || hourlyCost == null) {
    return <span className="text-xs text-gray-300">—</span>;
  }

  const value = Number(project.value);
  const p = computeProfitability(value, project.clickupTrackedMs ?? 0, hourlyCost);
  const pct = p.costRatio != null ? Math.round(p.costRatio * 100) : null;
  const tone =
    p.costRatio == null ? 'text-gray-400'
      : p.costRatio > 1 ? 'text-red-600'
        : p.costRatio > 0.75 ? 'text-yellow-600'
          : 'text-green-600';

  return (
    <div title={`${formatTracked(project.clickupTrackedMs)} tracked · ${formatCurrency(p.cost)} of ${formatCurrency(value)}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${costBarColor(p.costRatio)}`} style={{ width: `${Math.min(p.costRatio ?? 0, 1) * 100}%` }} />
        </div>
        <span className={`text-xs font-semibold ${tone}`}>{pct != null ? `${pct}%` : '—'}</span>
      </div>
      {detailed && (
        <p className="text-xs text-gray-400 mt-0.5">
          {formatCurrency(p.cost)} of {formatCurrency(value)}
        </p>
      )}
    </div>
  );
}
